import { fromJS } from 'immutable';
import _ from 'lodash';

import { findPropertiesByType, populateMissingSchemaValues } from '@pearsontechnology/aero-plugin-schema';

import activityService from 'services/ActivityService';
import interactiveService from 'services/InteractiveService';
import PreviewService from 'services/PreviewService';
import componentService from 'services/ComponentService';
import pluginService from 'services/PluginService';
import pathwayService from 'services/PathwayService';

import enhanceWithConfigResolver from './enhanceWithConfigResolver';
import * as coursewareTypes from '../constants/coursewareTypes';
import { VIEWS } from 'Workspace/components/PreviewDialog/constants';

const mockProgress = {
  attempt: {
    confidence: 0,
    value: 1,
  },
  completion: {
    confidence: 0,
    value: 0,
  },
  score: {
    value: 0,
  },
};

function getJS(data) {
  return data && data.toJS
    ? data.toJS()
    : data;
}

class CoursewareItemServices {
  constructor({ context, id, coursewareType, target = {} }) {
    this.context = context;
    this.id = target.id || id;
    this.coursewareType = target.type || coursewareType;

    const service = {
      [coursewareTypes.ACTIVITY]: {
        getCoursewareItem: activityService.getActivity,
        getInConfig: activityService.getInActivityConfig,
        saveInConfig: activityService.saveInActivityConfig,
        saveInScope: activityService.saveInActivityPreviewScope,
        subscribeToConfig: activityService.subscribeToActivityConfigPart,
        getSchemasIn: activityService.getSchemasInActivity,
        getPlugin: pluginService.getPlugin,
        getPathway: pathwayService.getPathway,
      },
      [coursewareTypes.INTERACTIVE]: {
        getCoursewareItem: interactiveService.getInteractive,
        getInConfig: interactiveService.getInInteractiveConfig,
        saveInConfig: interactiveService.saveInInteractiveConfig,
        saveInScope: interactiveService.saveInInteractivePreviewScope,
        subscribeToConfig: interactiveService.subscribeToInteractiveConfig,
        getSchemasIn: interactiveService.getSchemasInInteractive,
        getPlugin: pluginService.getPlugin,
        getPathway: pathwayService.getPathway,
      },
      [coursewareTypes.COMPONENT]: {
        getCoursewareItem: componentService.getComponent,
        getInConfig: componentService.getInComponentConfig,
        saveInConfig: componentService.saveInComponentConfig,
        saveInScope: componentService.saveInComponentPreviewScope,
        subscribeToConfig: componentService.subscribeToComponentConfig,
        getSchemasIn: componentService.getSchemaForComponent,
        getPlugin: pluginService.getPlugin,
        getPathway: pathwayService.getPathway,
      },
    }[this.coursewareType];

    if (!service) {
      throw new Error('Failed to create service', coursewareType);
    }
    const configData = {
      id: this.id,
      type: target.type || coursewareType,
    };
    this.service = enhanceWithConfigResolver(service, configData);
    // Expose the original data layer for CDP rawState
    this.rawService = service;
  }

  async _retrieveCoursewareSchema() {
    if (!this.pluginSchema) {
      const coursewareItem = await this.service.getCoursewareItem(this.id);

      const { plugin } = coursewareItem;
      const { pluginId, version } = plugin;

      const pluginResponse = await pluginService.getPlugin(pluginId, version);
      const { manifest } = pluginResponse;
      const { configurationSchema } = manifest;
      this.pluginSchema = JSON.parse(configurationSchema);
    }
    return this.pluginSchema;
  }

  _elementRefsToChildRefs(elements = []) {
    return elements.map((element) => {
      const { elementId, elementType } = element;
      return {
        itemId: elementId,
        itemType: elementType,
      };
    });
  }

  async _resolvePathwayItems(path = [], originalConfig) {
    const pluginSchema = await this._retrieveCoursewareSchema();
    const pathwayItems = findPropertiesByType(pluginSchema, 'pathway');

    let config;
    if (originalConfig === null || typeof originalConfig === 'undefined') {
      config = fromJS(await this.service.getInConfig(this.id, path));
    } else {
      config = fromJS(originalConfig);
    }

    config = populateMissingSchemaValues(pluginSchema, config, path);

    if (pathwayItems.find((schemaItem) => JSON.stringify(schemaItem.path) === JSON.stringify(path))) {
      const pathwayId = config.get('pathwayId');
      const pathway = pathwayId
        ? await pathwayService.getPathway(pathwayId)
        : {};
      pathway.children = this._changeElementPositionOfChildrenPathwaysForPreview(pluginSchema, pathway.children);
      return this._elementRefsToChildRefs(pathway.children);
    }

    if (path.length) {
      return getJS(config);
    }

    const pathwayPromises = pathwayItems.map((schemaItem) => {
      const pathwayId = config.getIn([...schemaItem.path, 'pathwayId']);
      return pathwayId
        ? pathwayService.getPathway(pathwayId)
        : {};
    });

    const pathwayResults = await Promise.all(pathwayPromises);

    let updatedConfig = config;
    pathwayItems.forEach((schemaItem, index) => {
      updatedConfig = updatedConfig.setIn(
        [...schemaItem.path],
        this._elementRefsToChildRefs(pathwayResults[index].children)
      );
    });
    return getJS(updatedConfig);
  }

  /**
   * This function changes the position of element in an array for Preview with type = subActivity,
   * For sub-activity if interactive id is matching with the url interactive id then that element
   * will always be as a top element in the array.
   * For non-preview elements/for non-sub-activity elements it returns childrenPathways without any change.
   * @param  pluginSchema
   * @param  childrenPathways
   * @returns childrenPathways
   */
  _changeElementPositionOfChildrenPathwaysForPreview (pluginSchema, childrenPathways) {
    const url = window.location.pathname;
    if(url.indexOf(VIEWS.PREVIEW) > -1 && pluginSchema.hasOwnProperty('groupActivityId')) {
      const interactiveId = url.match('screen/') && url.substr(url.lastIndexOf('/') + 1);
      return [...childrenPathways.filter((item) => item.elementId === interactiveId), ...childrenPathways.filter((item) => item.elementId !== interactiveId)];
    }
    return childrenPathways;
  }

  _findIdInComponents(coursewareItem, id) {
    const { components = [] } = coursewareItem;
    const foundComponent = components.find((
      (componentId) => componentId === id
    ));
    return foundComponent
      ? { itemId: id, itemType: coursewareTypes.COMPONENT }
      : undefined;
  }

  async _findInPathways(coursewareItem, id) {
    const { childrenPathways = [] } = coursewareItem;
    const pathways = await Promise.all(
      childrenPathways.map((pathwayId) => (
        pathwayService.getPathway(pathwayId)
      ))
    );

    const foundPathway = pathways.find((pathway) => (
      pathway.children.find((child) => child.elementId === id)
    ));

    return foundPathway
      ? {
        itemId: foundPathway.children[0].elementId,
        itemType: foundPathway.children[0].elementType,
      }
      : undefined;
  }

  evaluate(elementId) {
    return PreviewService.evaluateScenario(elementId);
  }

  async getState() {
    return this.service &&  await this.service.getCoursewareState() || this._resolvePathwayItems();
  }

  // Get configuration without data transformation like url to urn
  async getRawState() {
    if (this.service) {
      return await this.service.getCoursewareRawState(this.id);
    } else {
      const originalConfig = await this.rawService.getInConfig(this.id);

      return this._resolvePathwayItems(null, originalConfig);
    }
  }

  async getInState(path) {
    return this.service &&  this.service.getInCoursewareState(path) || this._resolvePathwayItems(path);
  }

  async getInRawState(path) {
    if (this.service) {
      return await this.service.getInCoursewareRawState(path);
    } else {
      const originalConfig = await this.rawService.getInConfig(this.id, path);
      return this._resolvePathwayItems(path, originalConfig);
    }
  }

  async setInState(path, value) {
    ({
      LEARNER: this.service.saveInScope,
      EDITOR: this.service.saveInConfig,
    })[this.context](this.id, path, value);
  }

  subscribeProxy = (path, fn) => async (config) => {
    fn(config);
  }

  async subscribeToState(path, fn) {
    if (this.service && this.service.subscribeToConfig) {
      return this.service.subscribeToConfig(
        this.id,
        path,
        this.subscribeProxy(path, fn),
      );
    }
    return () => { };
  }

  getInProgress(path) {
    return Promise.resolve(path ? mockProgress : _.get(mockProgress, path));
  }

  subscribeToProgress(path, callbackFn) {
    callbackFn(path ? mockProgress : _.get(mockProgress, path));
  }

  getResult() {
    return {};
  }
  
  subscribeToResult() {
    return;
  }

  static build(args) {
    return new CoursewareItemServices(args);
  }
}

export default CoursewareItemServices;
